import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Checkbox } from '@blueprintjs/core';

import Cell from './Cell';
import cls from './Row.module.css';

class HeadingRow extends PureComponent {
  static propTypes = {
    schema: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        grow: PropTypes.bool,
      })
    ),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      })
    ),
    selectable: PropTypes.bool,
    allRowsSelection: PropTypes.array,
    trProps: PropTypes.object,
    onAllRowsSelectionChange: PropTypes.func,
  };

  static defaultProps = {
    items: [],
    selectable: false,
    allRowsSelection: [],
    trProps: {},
    onAllRowsSelectionChange: () => {},
  };

  buildLabelRenderer = label => {
    if (typeof label === 'string') {
      return () => String(label).toUpperCase();
    }

    if (typeof label !== 'function') {
      return () => null;
    }

    return label;
  };

  renderSelectionCell = () => {
    const { items, allRowsSelection } = this.props;

    const allSelected = allRowsSelection.every(Boolean);
    const someSelected = !allSelected && allRowsSelection.some(Boolean);

    return (
      <Cell
        tagName="th"
        renderer={() => (
          <Checkbox
            className={cls.checkbox}
            large
            checked={allSelected}
            indeterminate={someSelected}
            data-cy="checkbox-table-heading-row"
          />
        )}
        item={items}
        rowIdx={null}
        cellProps={{
          className: cls.headingCell,
          onClick: this.props.onAllRowsSelectionChange,
        }}
      />
    );
  };

  render() {
    const { schema, selectable, trProps } = this.props;

    if (!schema) {
      return null;
    }

    return (
      <tr {...trProps}>
        {selectable && this.renderSelectionCell()}

        {schema.map(({ label = '', grow, headingCellProps }, idx) => (
          <Cell
            tagName="th"
            renderer={this.buildLabelRenderer(label)}
            grow={grow}
            cellProps={{
              ...headingCellProps,
              className: cx(cls.headingCell, headingCellProps?.className),
            }}
            key={idx}
          />
        ))}
      </tr>
    );
  }
}

export default class extends PureComponent {
  static propTypes = {
    schema: PropTypes.arrayOf(PropTypes.object),
    item: PropTypes.shape({
      id: PropTypes.string,
    }),
    idx: PropTypes.number,
    selectable: PropTypes.bool,
    selected: PropTypes.bool,
    rowProps: PropTypes.object,
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onRowSelectionChange: PropTypes.func,
    onHover: PropTypes.func,
    onMouseLeave: PropTypes.func,
  };

  static defaultProps = {
    selectable: false,
    selected: false,
    rowProps: {},
    onClick: () => {},
    onDoubleClick: () => {},
    onRowSelectionChange: () => {},
    onHover: () => {},
    onMouseLeave: () => {},
  };

  static Heading = HeadingRow;

  selectionCellRenderer = () => {
    const { selected } = this.props;

    return <Checkbox className={cls.checkbox} large checked={selected} data-cy="checkbox-table-row" />;
  };

  onClick = event => {
    const { item, idx } = this.props;

    this.props.onClick(item, idx, event);
  };

  onDoubleClick = event => {
    const { item, idx } = this.props;

    this.props.onDoubleClick(item, idx, event);
  };

  handleHover = () => {
    const { onHover, idx } = this.props;
    onHover(idx);
  };

  handleMouseLeave = () => {
    const { onMouseLeave } = this.props;
    onMouseLeave();
  };

  render() {
    const { schema, item, idx, selectable, rowProps, onRowSelectionChange, hoveredRow } = this.props;

    if (!schema || !item) {
      return null;
    }

    const { after, ...trProps } = rowProps;

    const rowLength = schema.length + (selectable ? 1 : 0);

    const extendedItem = { ...item, hoveredRow: hoveredRow, isHovered: hoveredRow === idx };

    return (
      <>
        <tr
          {...trProps}
          onClick={this.onClick}
          onDoubleClick={this.onDoubleClick}
          onMouseEnter={this.handleHover}
          onMouseLeave={this.handleMouseLeave}
        >
          {selectable && (
            <Cell
              renderer={this.selectionCellRenderer}
              item={extendedItem}
              rowIdx={idx}
              cellProps={{
                onClick: onRowSelectionChange,
              }}
            />
          )}

          {schema.map((cellSchema, idx) => (
            <Cell {...cellSchema} item={extendedItem} key={idx} />
          ))}
        </tr>

        {after && (
          <tr className={cls.afterRow}>
            <td colSpan={rowLength}>{after}</td>
          </tr>
        )}
      </>
    );
  }
}
